import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

// Custom validator to check passwords match
function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
        confirmPassword.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
    } else {
        if (confirmPassword?.errors?.['passwordMismatch']) {
            const errors = { ...confirmPassword.errors };
            delete errors['passwordMismatch'];
            confirmPassword.setErrors(Object.keys(errors).length ? errors : null);
        }
        return null;
    }
}

@Component({
    selector: 'app-force-change-password',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './force-change-password.component.html',
    styleUrl: './force-change-password.component.scss'
})
export class ForceChangePasswordComponent implements OnInit {
    changePasswordForm!: FormGroup;
    isLoading = false;
    errorMessage = '';
    successMessage = '';

    showTempPassword = false;
    showNewPassword = false;
    showConfirmPassword = false;

    // Password strength
    passwordStrength: 'weak' | 'medium' | 'strong' | '' = '';
    passwordStrengthScore = 0;

    private readonly API_URL = environment.apiUrl;

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private router: Router,
        private http: HttpClient,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.initializeForm();
    }

    private initializeForm(): void {
        this.changePasswordForm = this.fb.group({
            tempPassword: ['', [Validators.required, Validators.minLength(6)]],
            newPassword: ['', [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/)
            ]],
            confirmPassword: ['', [Validators.required]]
        }, { validators: passwordMatchValidator });

        // Watch new password to evaluate strength
        this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(value => {
            this.evaluatePasswordStrength(value);
        });
    }

    private evaluatePasswordStrength(password: string): void {
        if (!password) {
            this.passwordStrength = '';
            this.passwordStrengthScore = 0;
            return;
        }

        let score = 0;
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;

        this.passwordStrengthScore = Math.min(score, 6);

        if (score <= 2) {
            this.passwordStrength = 'weak';
        } else if (score <= 4) {
            this.passwordStrength = 'medium';
        } else {
            this.passwordStrength = 'strong';
        }
    }

    get strengthBarWidth(): string {
        return `${(this.passwordStrengthScore / 6) * 100}%`;
    }

    toggleVisibility(field: 'temp' | 'new' | 'confirm'): void {
        if (field === 'temp') this.showTempPassword = !this.showTempPassword;
        if (field === 'new') this.showNewPassword = !this.showNewPassword;
        if (field === 'confirm') this.showConfirmPassword = !this.showConfirmPassword;
    }

    onSubmit(): void {
        if (this.changePasswordForm.invalid) {
            this.markFormGroupTouched();
            return;
        }

        const { tempPassword, newPassword } = this.changePasswordForm.value;
        const currentUser = this.authService.getCurrentUser();

        if (!currentUser?.id) {
            this.errorMessage = 'ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่';
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';
        this.successMessage = '';

        this.http.post(`${this.API_URL}/users/${currentUser.id}/force-change-password`, {
            oldPassword: tempPassword,
            newPassword: newPassword
        }).subscribe({
            next: () => {
                this.isLoading = false;
                this.successMessage = 'เปลี่ยนรหัสผ่านสำเร็จ! กำลังนำท่านไปหน้าหลัก...';
                this.cdr.detectChanges();

                // Update forcePasswordChange flag in both localStorage and AuthService
                const updatedUser = { ...currentUser, forcePasswordChange: false };
                this.authService.updateCurrentUser(updatedUser);
                console.log('✅ Updated forcePasswordChange to false in localStorage and AuthService');

                setTimeout(() => {
                    // Redirect based on user type
                    if (this.authService.isStaff()) {
                        this.router.navigate(['/staff/dashboard']);
                    } else {
                        // For regular users, check if user-information exists
                        this.checkUserInformationAndRedirect(currentUser.id);
                    }
                }, 2000);
            },
            error: (error) => {
                this.isLoading = false;
                this.handleError(error);
                this.cdr.detectChanges();
            }
        });
    }

    /**
     * Check if user-information exists and redirect accordingly
     * If 404, redirect to /register, otherwise to /user/dashboard
     */
    private checkUserInformationAndRedirect(userId: string): void {
        console.log('🔍 Checking user-information for redirect after password change');
        this.http.get(`${this.API_URL}/user-information/user/${userId}`).subscribe({
            next: () => {
                console.log('✅ User-information exists - redirecting to dashboard');
                this.router.navigate(['/user/dashboard']);
            },
            error: (error) => {
                if (error.status === 404) {
                    console.log('⚠️ User-information not found (404) - redirecting to register');
                    this.router.navigate(['/register']);
                } else {
                    console.error('❌ Error checking user-information:', error);
                    // For other errors, redirect to dashboard and let it handle the error
                    this.router.navigate(['/user/dashboard']);
                }
            }
        });
    }

    private handleError(error: any): void {
        if (error.status === 401) {
            this.errorMessage = 'รหัสผ่านชั่วคราวไม่ถูกต้อง กรุณาตรวจสอบรหัสในอีเมลและลองใหม่อีกครั้ง';
        } else if (error.status === 400) {
            this.errorMessage = error.error?.message || 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่อีกครั้ง';
        } else if (error.status === 0) {
            this.errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
        } else {
            this.errorMessage = 'เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง';
        }
    }

    private markFormGroupTouched(): void {
        Object.keys(this.changePasswordForm.controls).forEach(key => {
            const control = this.changePasswordForm.get(key);
            control?.markAsTouched();
        });
    }

    // Getters
    get tempPassword() { return this.changePasswordForm.get('tempPassword'); }
    get newPassword() { return this.changePasswordForm.get('newPassword'); }
    get confirmPassword() { return this.changePasswordForm.get('confirmPassword'); }

    // Password requirement helpers (regex literals can't be used in Angular templates)
    get newPasswordValue(): string { return this.newPassword?.value || ''; }
    get hasMinLength(): boolean { return this.newPasswordValue.length >= 8; }
    get hasLowercase(): boolean { return /[a-z]/.test(this.newPasswordValue); }
    get hasUppercase(): boolean { return /[A-Z]/.test(this.newPasswordValue); }
    get hasNumber(): boolean { return /[0-9]/.test(this.newPasswordValue); }
}
